import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { MinMaxStep, SectionChange, SectionModelType } from '../../types/section.type';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { getDifficultyLevel, getSectionBlockGradient } from '../../utils/section-difficulty.utils';
import { durationToSeconds } from '../../utils/duration.utils';
import { MIN_SECTION_DURATION_SECONDS } from '../../configs/sections-editor/default-section.config';
import { snapToStep, valueToHeightPercent } from '../../utils/sections-editor.utils';
import { contentWidth } from '../../utils/dom.utils';
import { DIFFICULTY_LEVEL_COLORS } from '../../configs/sections-editor/workout-mode-colors.config';
import { WorkoutDifficultyLevel } from '../../types/workout-difficulty.type';
import { Keydown } from '../../directives/keydown';

@Component({
  selector: 'app-section-block',
  imports: [],
  host: {
    '(mouseleave)': `isHovered.set(isOnResize())`,
    '[style.width.%]': 'widthPercent()',
  },
  template: `
    <div
      class="section-block"
      (mouseenter)="onMouseEnterBloc()"
      (mouseleave)="onMouseLeaveBloc()"
      (click)="onBlockClick()"
      (contextmenu)="onBlockContextMenu($event)"
      (mousedown)="$event.shiftKey && $event.preventDefault()"
      [style.background]="blocDifficultyGradientColor()"
      [style.height]="displayedHeight() + '%'"
    >
      <div
        class="border-top"
        [style.background]="blocDifficultyBorderTopColor()"
        [class.is-on-resize]="onHeightResize()"
      ></div>
      <div class="border-right" [class.is-on-resize]="onWidthResize()"></div>
      @if (isHovered() || isOnResize()) {
        <div
          class="resize-bar top"
          (pointerdown)="onResizeTopStart($event)"
          (pointermove)="onResizeTopMove($event)"
          (pointerup)="onResizeTopEnd($event)"
          (pointercancel)="onResizeTopEnd($event)"
          [class.is-on-resize]="onHeightResize()"
        ></div>
        <div
          class="resize-bar right"
          (pointerdown)="onResizeRightStart($event)"
          (pointermove)="onResizeRightMove($event)"
          (pointerup)="onResizeRightEnd($event)"
          (pointercancel)="onResizeRightEnd($event)"
          [class.is-on-resize]="onWidthResize()"
        ></div>
      }
    </div>
    @if (secondaryHeight(); as height) {
      <div class="secondary-line" [style.bottom.%]="height.current"></div>
      @if (height.previous !== undefined && height.previous !== height.current) {
        <div
          class="secondary-connector"
          [style.bottom]="'calc(' + Math.min(height.previous, height.current) + '% - 1px)'"
          [style.height]="'calc(' + Math.abs(height.previous - height.current) + '% + 2px)'"
        ></div>
      }
    }
  `,
  styleUrl: './section-block.scss',
})
export class SectionBlock {
  private element: ElementRef<HTMLElement> = inject(ElementRef);
  public sectionEditorService: SectionsEditorService = inject(SectionsEditorService);
  private keydown: Keydown = inject(Keydown);

  mode: InputSignal<WorkoutMode | undefined> = input.required<WorkoutMode | undefined>();
  section: InputSignal<SectionModelType> = input.required<SectionModelType>();
  /** Section a gauche du bloc : le trait de la 2e valeur la rejoint par un segment vertical. */
  previousSection: InputSignal<SectionModelType | undefined> = input<SectionModelType>();
  sectionChangeStart: OutputEmitterRef<void> = output<void>();
  sectionChange: OutputEmitterRef<SectionChange> = output<SectionChange>();
  sectionChangeEnd: OutputEmitterRef<void> = output<void>();
  timelineDuration: InputSignal<number> = input.required<number>();
  isOnResize: Signal<boolean> = computed(() => this.onWidthResize() || this.onHeightResize());

  isHovered: WritableSignal<boolean> = signal(false);
  isSelected: Signal<boolean> = computed(() =>
    this.sectionEditorService.selectedSectionsIds().includes(this.section().id),
  );
  // Survol du bloc lui-même ou de la row associée
  isHighlighted: Signal<boolean> = computed(
    () => this.isHovered() || this.sectionEditorService.hoveredSectionId() === this.section().id,
  );

  private ignoreNextClick: boolean = false;

  onBlockClick(): void {
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return;
    }
    this.sectionEditorService.selectSection(this.section().id, {
      toggle: this.keydown.isCtrlOrMetaPressed(),
      range: this.keydown.isShiftPressed(),
    });
  }

  /** Sur Mac, Ctrl + clic declenche un clic droit et jamais de `click`. */
  onBlockContextMenu(event: MouseEvent): void {
    if (!event.ctrlKey) return;
    event.preventDefault();
    this.sectionEditorService.selectSection(this.section().id, {
      toggle: true,
      range: this.keydown.isShiftPressed(),
    });
  }

  onMouseEnterBloc(): void {
    this.isHovered.set(true);
    this.sectionEditorService.hoveredSectionId.set(this.section().id);
  }

  onMouseLeaveBloc(): void {
    this.isHovered.set(false);
    this.sectionEditorService.hoveredSectionId.set(undefined);
  }

  onHeightResize: WritableSignal<boolean> = signal(false);
  onWidthResize: WritableSignal<boolean> = signal(false);

  /** Niveau de difficulte du bloc, sur la valeur affichee en bloc (la premiere du mode). */
  difficulty: Signal<WorkoutDifficultyLevel> = computed(() => {
    const [primary]: { type: WorkoutMode; value: number }[] =
      this.sectionEditorService.sectionDatas(this.section(), this.mode());
    return getDifficultyLevel(primary.value, primary.type);
  });

  protected blocDifficultyGradientColor: Signal<string> = computed(() => {
    if (this.isSelected()) return this.blocDifficultyBorderTopColor();
    if (this.isHighlighted()) return this.blocDifficultyHoverColor();
    return this.gradientColor();
  });

  private readonly blocColor: Signal<string> = computed(
    () => DIFFICULTY_LEVEL_COLORS[this.difficulty()],
  );

  protected gradientColor: Signal<string> = computed(() =>
    getSectionBlockGradient(this.blocColor()),
  );

  protected blocDifficultyBorderTopColor: Signal<string> = this.blocColor;

  protected blocDifficultyHoverColor: Signal<string> = computed(
    () => `color-mix(in srgb, ${this.blocColor()} 50%, transparent 50%)`,
  );

  /** Valeur de la section dans l'unite du mode (%FTP, watts, km/h...). */
  value: Signal<number> = computed(() => {
    const section: SectionModelType = this.section();
    return this.sectionEditorService.sectionDatas(section, this.mode())[0].value;
  });

  /** Hauteur du bloc en % du graphe : la valeur est projetee sur l'axe [min, max]. */
  displayedHeight: Signal<number> = computed(() =>
    valueToHeightPercent(
      this.value(),
      this.sectionEditorService.min(),
      this.sectionEditorService.max(),
    ),
  );

  /**
   * Hauteurs (en % du graphe) du trait de la 2e valeur, pour ce bloc et le precedent,
   * sur l'axe de droite. `undefined` si le mode n'a qu'une valeur.
   */
  secondaryHeight: Signal<{ current: number; previous: number | undefined } | undefined> = computed(
    () => {
      const bounds: Readonly<MinMaxStep> | undefined =
        this.sectionEditorService.secondaryMinMaxStep();
      if (!bounds) return undefined;
      const toHeight = (section: SectionModelType): number | undefined => {
        const value: number | undefined = this.sectionEditorService.sectionDatas(
          section,
          this.mode(),
        )[1]?.value;
        return value === undefined
          ? undefined
          : valueToHeightPercent(value, bounds.min, bounds.max);
      };
      const current: number | undefined = toHeight(this.section());
      if (current === undefined) return undefined;
      const previous: SectionModelType | undefined = this.previousSection();
      return { current, previous: previous && toHeight(previous) };
    },
  );

  protected readonly Math: Math = Math;

  durationSeconds: Signal<number> = computed(() => durationToSeconds(this.section().duration));

  /** Largeur du bloc en % du graphe, proportionnelle a sa duree. */
  widthPercent: Signal<number> = computed(
    () => (this.durationSeconds() / this.timelineDuration()) * 100,
  );

  private readonly minDurationSeconds: number = MIN_SECTION_DURATION_SECONDS;

  private resizeStartY: number = 0;
  private resizeStartValue: number = 0;
  private resizeStartX: number = 0;
  private resizeStartDuration: number = 0;
  private resizeContainerWidth: number = 0;
  private resizeTimelineDuration: number = 0;
  private lastDelta: number = 0;

  onResizeTopStart(event: PointerEvent): void {
    this.startPointerCapture(event);
    this.resizeStartY = event.clientY;
    this.resizeStartValue = this.value();
    this.onHeightResize.set(true);
  }

  onResizeTopMove(event: PointerEvent): void {
    if (!this.onHeightResize()) return;
    const min: number = this.sectionEditorService.min();
    const max: number = this.sectionEditorService.max();
    const containerHeight: number = this.element.nativeElement.offsetHeight;
    const deltaY: number = this.resizeStartY - event.clientY;
    const deltaValue: number = (deltaY / containerHeight) * (max - min);
    const step: number = this.sectionEditorService.step();
    const newValue: number = snapToStep(this.resizeStartValue + deltaValue, min, max, step);
    this.emitDelta({ field: 'data', delta: newValue - this.resizeStartValue });
  }

  onResizeTopEnd(event: PointerEvent): void {
    if (!this.onHeightResize()) return;
    this.endPointerCapture(event);
    this.onHeightResize.set(false);
  }

  onResizeRightStart(event: PointerEvent): void {
    this.startPointerCapture(event);
    this.resizeStartX = event.clientX;
    this.resizeStartDuration = this.durationSeconds();
    this.resizeTimelineDuration = this.timelineDuration();
    this.resizeContainerWidth = contentWidth(this.element.nativeElement.parentElement);
    this.onWidthResize.set(true);
  }

  onResizeRightMove(event: PointerEvent): void {
    if (!this.onWidthResize() || !this.resizeContainerWidth) return;
    const deltaX: number = event.clientX - this.resizeStartX;
    const deltaSeconds: number = (deltaX / this.resizeContainerWidth) * this.resizeTimelineDuration;
    const newDuration: number = Math.max(
      this.minDurationSeconds,
      Math.round(this.resizeStartDuration + deltaSeconds),
    );
    console.log(deltaSeconds);

    this.emitDelta({ field: 'duration', delta: newDuration - this.resizeStartDuration });
  }

  onResizeRightEnd(event: PointerEvent): void {
    if (!this.onWidthResize()) return;
    this.endPointerCapture(event);
    this.onWidthResize.set(false);
  }

  private emitDelta(change: Exclude<SectionChange, { field: 'cadence' }>): void {
    if (change.delta === this.lastDelta) return;
    this.lastDelta = change.delta;
    this.sectionChange.emit(change);
  }

  /**
   * Le pointer capture garde les `pointermove` sur la barre meme quand le curseur
   * sort du bloc ou de la fenetre.
   */
  private startPointerCapture(event: PointerEvent): void {
    event.preventDefault();
    this.ignoreNextClick = true;
    this.lastDelta = 0;
    this.sectionChangeStart.emit();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.isHovered.set(true);
  }

  private endPointerCapture(event: PointerEvent): void {
    const target: HTMLElement = event.target as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
    this.isHovered.set(this.element.nativeElement.matches(':hover'));
    this.sectionChangeEnd.emit();
    setTimeout(() => (this.ignoreNextClick = false));
  }
}
